/**
 * Le regole del tesseramento che non dipendono dal server.
 *
 * Qui non c'è la *forma* dei dati — quella viene dallo schema, in
 * `@pedigreesmart/api-client` — ma le domande che l'interfaccia deve poter
 * fare su di essi: questa persona è in regola? quest'anno è pagato? Sono
 * funzioni pure perché sono anche le uniche cose di questa sezione che si
 * possono provare senza un server acceso.
 *
 * `core` non dipende da nessun pacchetto, quindi i tipi qui sono strutturali:
 * descrivono il minimo che serve, e il tipo generato ci rientra.
 */

/** Il minimo che serve sapere di un anno per giudicarlo. */
export interface AnnoTesseramento {
  year: number
  valid_from?: string
  valid_to?: string
  fee?: number | null
  paid_at?: string | null
}

/** Lo stato di un anno, in ordine di gravità crescente per la segreteria. */
export type StatoQuota = 'pagata' | 'da-pagare' | 'gratuita'

/**
 * Pagata, da pagare, o gratuita.
 *
 * La distinzione fra «gratuita» e «da pagare» conta: un anno senza quota non è
 * un anno insoluto — succede con i soci onorari e con le quote condonate — e
 * mostrarlo in rosso manderebbe la segreteria a sollecitare chi non deve
 * niente. Una quota di zero è una decisione; una quota assente e non pagata è
 * un sospeso.
 */
export function statoQuota(anno: AnnoTesseramento): StatoQuota {
  if (anno.paid_at) return 'pagata'
  if (anno.fee === 0) return 'gratuita'

  return 'da-pagare'
}

export function etichettaStatoQuota(stato: StatoQuota): string {
  switch (stato) {
    case 'pagata':
      return 'Quota pagata'
    case 'gratuita':
      return 'Nessuna quota dovuta'
    case 'da-pagare':
      return 'Quota da versare'
  }
}

/**
 * L'anno di tesseramento valido a una certa data.
 *
 * Il confronto è sulle date di validità e **non sul numero dell'anno**: le
 * associazioni non usano tutte l'anno solare, e ce ne sono che aprono la
 * campagna a settembre. Confrontare `year` con l'anno di sistema darebbe la
 * risposta giusta solo per chi segue il calendario, e sbagliata in silenzio
 * per gli altri.
 *
 * Se le date mancano si ricade sull'anno solare, che è meglio di niente.
 */
export function annoValidoA<T extends AnnoTesseramento>(
  anni: readonly T[],
  quando: Date,
): T | null {
  const giorno = quando.toISOString().slice(0, 10)

  return (
    anni.find((anno) => {
      if (anno.valid_from && anno.valid_to) {
        return anno.valid_from.slice(0, 10) <= giorno && giorno <= anno.valid_to.slice(0, 10)
      }

      return anno.year === quando.getFullYear()
    }) ?? null
  )
}

/**
 * Se il tesseramento è in regola a una certa data.
 *
 * «In regola» vuol dire due cose insieme: che esiste un anno che copre quella
 * data **e** che la quota risulta assolta. Un anno aperto ma non pagato non è
 * un tesseramento valido, ed è esattamente il caso che la segreteria deve
 * vedere a colpo d'occhio.
 */
export function inRegola(anni: readonly AnnoTesseramento[], quando: Date): boolean {
  const anno = annoValidoA(anni, quando)

  return null !== anno && 'da-pagare' !== statoQuota(anno)
}

/**
 * Il prossimo anno da aggiungere.
 *
 * Proposta, non regola: è solo il valore iniziale del campo, e resta
 * modificabile. Serve perché il caso normale — la campagna dell'anno in corso
 * — non richieda di digitare niente, e quello anomalo resti possibile.
 */
export function annoProposto(anni: readonly AnnoTesseramento[], oggi: Date): number {
  const massimo = anni.reduce((piu, anno) => Math.max(piu, anno.year), 0)

  return Math.max(massimo + 1, oggi.getFullYear())
}
